import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DropdownOption } from '@storybook/components/dropdown/dropdown.component';
import { ProcessTimeType } from '@pdqdev/pdq-shared/dist/interfaces';
import { Shippings, ShippingMethod } from '@pages/checkout-app/types';
import {
  UntypedFormGroup,
  UntypedFormControl,
  Validators,
  ValidatorFn,
  ValidationErrors,
  AbstractControl,
} from '@angular/forms';
import { CheckoutActions } from '@pages/checkout-app/checkout-store/actions';
import { Store } from '@ngrx/store';
import * as fromCheckoutReducer from '@pages/checkout-app/checkout-store/reducers/checkout.reducer';
import { takeUntil } from 'rxjs/operators';
import { ofType, Actions } from '@ngrx/effects';
import { Subject } from 'rxjs';
import { ToastService } from '@storybook/services/toast/toast.service';
import { CheckoutSettings } from '@pdqdev/pdq-shared/dist/entities';

export interface PartialShipping {
  id: string;
  name: string;
  disabled: boolean;
}

const ProcessTimeTypeOptions = [
  {
    label: 'day(s)',
    value: ProcessTimeType.DAYS,
  },
  {
    label: 'hour(s)',
    value: ProcessTimeType.HOURS,
    disabled: true,
  },
];

function processTimeShippingIdsValidator(editMode: boolean = false): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (editMode) {
      return null;
    }
    const value: string[] = control.value;
    return value?.length > 0 ? null : { missingIds: 'At least one method must be selected' };
  };
}

@Component({
  selector: 'manage-process-time',
  templateUrl: './manage-process-time-dialog.component.html',
  styleUrls: ['./manage-process-time-dialog.component.scss'],
  standalone: false,
})
export class ManageProcessTimeDialogComponent implements OnInit, OnDestroy {
  loadSettings$ = this.store.select(fromCheckoutReducer.selectSettings);
  isLoading$ = this.store.select(fromCheckoutReducer.selectIsLoading);
  processTimeTypeOptions: DropdownOption[] = ProcessTimeTypeOptions;
  processTimeFormGroup: UntypedFormGroup;
  checkoutSettings: CheckoutSettings;

  submitted: boolean = false;
  isEditMode: boolean = false;
  destroyed$ = new Subject();

  constructor(
    public dialogRef: MatDialogRef<ManageProcessTimeDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { shippings: Shippings[] },
    private store: Store<fromCheckoutReducer.State>,
    private actions$: Actions,
    private toastService: ToastService,
  ) {}

  ngOnInit() {
    this.initCheckoutSettingsFields();
    this.detectSettingsActionSuccess();
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  detectSettingsActionSuccess() {
    this.actions$
      .pipe(
        takeUntil(this.destroyed$),
        ofType(CheckoutActions.saveCheckoutSettingsSuccess, CheckoutActions.editCheckoutSettingsSuccess),
      )
      .subscribe(() => {
        this.dialogRef.close();
      });
  }

  initCheckoutSettingsFields() {
    this.loadSettings$.pipe(takeUntil(this.destroyed$)).subscribe((settings) => {
      this.isEditMode = !!settings?.id;
      this.checkoutSettings = Object.assign({}, settings);
      this.processTimeFormGroup = this.initForm(this.checkoutSettings);
    });
  }

  initForm(settings: CheckoutSettings): UntypedFormGroup {
    return new UntypedFormGroup({
      processingTime: new UntypedFormControl(settings?.processingTime ?? '', [
        Validators.min(this.isEditMode ? 0 : 1),
        Validators.required,
      ]),
      processingTimeType: new UntypedFormControl(settings?.processingTimeType ?? ProcessTimeType.DAYS),
      processingTimeShippingIds: new UntypedFormControl(
        settings?.processingTimeShippingIds ?? [],
        processTimeShippingIdsValidator(this.isEditMode),
      ),
    });
  }

  onCheckAll(value) {
    if (this.data?.shippings) {
      if (!this.processTimeFormGroup.value?.processingTimeShippingIds?.length && !!value) {
        const selectAllShippingIds = this.data?.shippings?.flatMap((shipping) =>
          shipping.shippingMethod?.category !== ShippingMethod.LOCAL ? shipping.id?.toString() : [],
        );
        this.processTimeFormGroup.controls['processingTimeShippingIds'].setValue(selectAllShippingIds);
      } else {
        this.processTimeFormGroup.controls['processingTimeShippingIds'].setValue([]);
      }
    }
  }

  onCheckedShipping(checked: boolean, mappedShipping: PartialShipping) {
    const selectedShippingsIds: string[] = [...(this.processTimeFormGroup.value?.processingTimeShippingIds ?? [])];
    if (checked && !selectedShippingsIds?.includes(mappedShipping.id)) {
      selectedShippingsIds.push(mappedShipping?.id);
    } else {
      const targetIndex = selectedShippingsIds?.findIndex((id) => id == mappedShipping.id);
      if (targetIndex > -1) {
        selectedShippingsIds.splice(targetIndex, 1);
      }
    }
    this.processTimeFormGroup.controls['processingTimeShippingIds'].setValue(selectedShippingsIds);
  }

  onSave(): void {
    this.submitted = true;
    const values = { ...this.processTimeFormGroup.value };
    if (this.processTimeFormGroup.valid) {
      if (this.checkoutSettings?.id) {
        // if id exists , so we modify current one.
        values.id = this.checkoutSettings.id;
        this.store.dispatch(CheckoutActions.editCheckoutSettings({ settings: values }));
      } else {
        // create new
        this.store.dispatch(CheckoutActions.saveCheckoutSettings({ settings: values }));
      }
    }
  }
}
